import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './requisitionsModuleConfig';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';

export interface SpareRequisitionLine {
  id?: number;
  itemId?: number | null;
  itemVcode?: string | null;
  itemDescr?: string | null;
  qty?: number | null;
  itemNotes?: string | null;
  item?: {
    level1?: string | null;
    level2?: string | null;
    level3?: string | null;
  } | null;
  lastOrdered?: {
    purchaseOrderId?: number | null;
    purchaseOrderRef?: string | null;
    orderDate?: string | null;
    qty?: number | null;
  } | null;
  lastRequested?: {
    requisitionId?: number | null;
    requisitionRef?: string | null;
    reqDate?: string | null;
    qty?: number | null;
  } | null;
}

export type RequisitionSaveStatus = 'DRAFT' | 'SAVED';

export interface SpareRequisitionTemplateLine {
  id?: number;
  itemId?: number | null;
  itemVcode?: string | null;
  itemDescr?: string | null;
  qty?: number | null;
  itemNotes?: string | null;
  item?: { level1?: string | null; level2?: string | null; level3?: string | null } | null;
}

export interface SpareRequisitionTemplate {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  lines?: SpareRequisitionTemplateLine[];
}

export interface SpareRequisition {
  id: number;
  reqDate?: string | null;
  caseId?: number | null;
  vesselId?: number | null;
  categoryIds?: number[];
  ref?: string | null;
  refN?: number | null;
  notes?: string | null;
  saveStatus?: RequisitionSaveStatus | null;
  createdAt: string;
  updatedAt: string;
  lines?: SpareRequisitionLine[];
  _count?: { lines: number };
  spareCase?: {
    id: number;
    ref?: string | null;
    caseDate?: string | null;
    eta?: string | null;
    port?: { name?: string | null; code?: string | null } | null;
  } | null;
  vessel?: { id: number; vesselName: string } | null;
  categories?: { id: number; name: string; abrv?: string | null }[];
  pipelineCounts?: {
    enquiryCount?: number;
    quotationCount?: number;
    orderCount?: number;
    deliveryCount?: number;
  };
}

export const useSpareRequisitions = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<SpareRequisition> | SpareRequisition[]
  >(
    `${moduleConfig.module.api.list}${q}`,
    fetcher,
    { revalidateOnFocus: true },
  );
  const normalized = normalizePaginatedListResponse<SpareRequisition>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useSpareRequisition = (id?: number) => {
  const { data, error, isLoading, mutate } = useSWR<SpareRequisition>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};

